import { type CartForm, type CartFormEntry, downloadFile, getReportLabels, ReportType } from "./api/downloadCartReport";
import { type ThunkAction } from "redux-thunk";
import { type RootState } from "../../../../../../../redux/store";
import { type AnyAction } from "../../../../../../../redux/types";
import { toMapFn } from "../../../../../../../utils/reducers";
import { getAliases } from "../../../../../../../common/components/Product/ProductAliases";
import * as WeightUtils from "../../../../../../../utils/WeightUtils";
import { stockConfig } from "../../../../../../../common/components/StockIndicator/StockIndicator";
import dayjs from "dayjs";
import { DateFormatType, formatDate } from "../../../../../../../utils/DateFormatter";
import { msToMonths } from "../../../../../../../utils/dateUtils";
import { selectTotalCartPrice, selectTotalCartWeight } from "~/feature/cart/cart/redux/selectors/cartSelectors";
import { selectProductDescriptor } from "../../../../../../../redux/products/productsSelectors";
import { useDispatch } from "react-redux";
import { type LineItem, PackageEntrySource } from "../../../../models/CartData";
import { getPackagingLabelAtLineItem } from "~/feature/cart/cart/utils/cartUtils";
import { getTotalPriceForLineItem, isProductPMA } from "../../../../../../ProductDescriptor/v1/productDescriptorUtils";
import { isConfiguratorProduct } from "../../../../../../../models/ProductData";
import { resolveTranslationSync } from "../../../../../../../common/Translation";
import { useTranslation, type WithTranslation } from "react-i18next";
import { getOrderType, translateWarehouseName } from "~/utils/language/translationUtils";
import { userIsSalesman } from "~/utils/roles";
import { FccAction, trackFccAction } from "~/FreeCapacityPush/api/trackFccAction";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { LinkButton } from "../../../../../../../common/components/Link";
import { Icon } from "../../../../../../../common/components/icon/Icon";
import { IconType } from "../../../../../../../common/components/icon/IconType";
import { UNIT_CONFIGURATOR_PRODUCT_CODE } from "~/Configurators/constants";

interface Props {
  type: ReportType;
  className?: string;
  disabled?: boolean;
  isB2BCart?: boolean;
}

export const DownloadReportButton = ({ type, className, disabled, isB2BCart = false }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const onClick = () => {
    dispatch(downloadReportAction(type, t));
  };

  return (
    <LinkButton
      className={className}
      onClick={onClick}
      disabled={disabled}
      data-test-id={getTestId(type)}
      iconBefore={isB2BCart ? <FileDownloadOutlinedIcon /> : <Icon type={IconType.Download} />}
    >
      {t(getLabelKey(type, isB2BCart))}
    </LinkButton>
  );
};

const getLabelKey = (type: ReportType, isB2BCart: boolean) => {
  switch (type) {
    case ReportType.CSV:
      return "downloadFile.type.csv";
    case ReportType.XLSX:
      if (isB2BCart) {
        return "downloadFile.excel.title";
      }
      return "downloadFile.type.excel";
    default:
      throw new Error("Not implemented");
  }
};

const getTestId = (type: ReportType) => {
  switch (type) {
    case ReportType.CSV:
      return "cart-btn-report-csv";
    case ReportType.XLSX:
      return "cart-btn-report-xslx";
    default:
      throw new Error("Not implemented");
  }
};

type ThunkResult<R> = ThunkAction<R, RootState, undefined, AnyAction>;

function downloadReportAction(type: ReportType, t: WithTranslation["t"]): ThunkResult<void> {
  return (_, getState) => {
    const state = getState();

    const packEntriesById = state.cart.packEntriesById;
    const productsTableInfo: CartFormEntry[] = Object.keys(packEntriesById).map((packEntryId) => {
      const packEntry = packEntriesById[packEntryId];

      const productCode = packEntry.productCode;

      const productDescriptor = selectProductDescriptor(state, {
        productCode,
        priceReference: packEntry.priceReference,
      });

      let packaging;
      if (packEntry.packSize === undefined) {
        packaging = "-";
      } else {
        packaging = getPackagingLabelAtLineItem({
          t,
          packCode: packEntry.packCode,
          packSize: packEntry.packSize,
          quantity: packEntry.quantity,
          productDescriptor: productDescriptor,
        }) as string;
      }

      let customerMaterialNumber;
      const aliases = getAliases(productDescriptor.base, "15-digits", packEntry.packCode);
      if (aliases.length > 0) {
        customerMaterialNumber = aliases.join(", ");
      } else {
        customerMaterialNumber = t("product.noCustomerMaterialNumber");
      }

      const netPrice = packEntry.netPrice?.value ?? null;
      const availability = packEntry.isInStock !== null ? t(stockConfig[packEntry.isInStock].translationKey) : "-";
      const priceReference = packEntry.priceReference ? getPriceReference(packEntry.priceReference) : "-";

      let edd: string = "-";
      if (packEntry.edd) {
        if (packEntry.edd.type === "replenishment") {
          const ms = dayjs(packEntry.edd.date).diff(new Date());
          const months = Math.ceil(msToMonths(ms));
          edd = t("estimatedDeliveryDate.replenishment", { count: months });
        } else {
          edd = formatDate(packEntry.edd.date, DateFormatType.DATE);
        }
      }

      let productDescription;
      if (isConfiguratorProduct(productDescriptor.base)) {
        if (productDescriptor.base.hybrisCode === UNIT_CONFIGURATOR_PRODUCT_CODE) {
          productDescription = getLinearProductDescription(
            t("cartPage.guidway.info", {
              count: Number(productDescriptor.base.guideway),
            }),
            t("cartPage.carriages.info", {
              count: Number(productDescriptor.base.carriages),
            }),
          );
        } else {
          productDescription = "-";
        }
      } else {
        productDescription = resolveTranslationSync(productDescriptor.base.shortDescription);
      }

      const isInCampaignScope = packEntry.saleCampaignEnabled ? "Yes" : "No";

      return {
        partNumber: productDescriptor.base.designation,
        matnr: productDescriptor.base.sapMaterialNumber,
        description: productDescription,
        quantity: packEntry.quantity,
        packaging: packaging,
        shipment: translateWarehouseName(t, packEntry.primaryWarehouseName),
        customerMaterialNumber: customerMaterialNumber,
        requestedDeliveryDate: packEntry.deliveryDate ? formatDate(packEntry.deliveryDate, DateFormatType.DATE) : "-",
        estimatedShippingDate: edd,
        availability: productDescriptor.base.quantityVisible && !isProductPMA(productDescriptor) ? availability : "",
        netPrice: netPrice,
        priceUnit: t("common.product.perPiece", {
          count: packEntry.netPrice?.unit ?? 1,
        }),
        totalNetPrice: getTotalPriceForLineItem(packEntry, productDescriptor)?.value ?? null,
        comment: packEntry.comment || "",
        priceReference: priceReference,
        alias15Digit: productDescriptor.base.sapMaterialNumber + packEntriesById[packEntryId].packCode,
        orderLineReference: buildOrderLineReferenceValue(packEntry, t),
        customerOrderNumber: packEntry.customerOrderNumber,
        inCampaignScope: isInCampaignScope,
        marketDesignation: productDescriptor.base.marketDesignation,
      };
    });

    const reportLabels = toMapFn(
      getReportLabels(),
      (key) => key,
      (key) => t(key),
    );

    const totalSum = selectTotalCartPrice(state);
    const totalWeight = selectTotalCartWeight(state);

    const apiBody: CartForm = {
      orderType: getOrderType(t, window.SCHA.config.selectedOrderType),
      totalPrice: totalSum ? totalSum.value : null,
      currency: totalSum ? totalSum.currencyIso : "",
      approxWeight: WeightUtils.text(totalWeight),
      cartEntries: productsTableInfo,
      reportLabels: reportLabels,
    };

    if (userIsSalesman()) {
      trackFccAction(type === ReportType.CSV ? FccAction.DownloadCsv : FccAction.DownloadExcel);
    }

    downloadFile(apiBody, type);
  };
}

const getLinearProductDescription = (guidewaysText: string, carriagesText: string): string => {
  return guidewaysText + carriagesText;
};

const getPriceReference = (packEntryPriceRef: string): string => {
  const configPriceRef = window.SCHA.config.priceReferences.find(
    (priceReference) => priceReference.value === packEntryPriceRef,
  );
  return configPriceRef ? configPriceRef.label : "-";
};

const buildOrderLineReferenceValue = (packEntry: LineItem, t: WithTranslation["t"]): string => {
  if (packEntry.source === PackageEntrySource.QUOTE) {
    if (packEntry.isBundled) {
      return t("cartPage.report.bundled.quote", { quote: packEntry.quoteNumber, line: packEntry.quoteLine });
    } else {
      return t("cartPage.report.quote", { quote: packEntry.quoteNumber, line: packEntry.quoteLine });
    }
  } else if (packEntry.source === PackageEntrySource.CONTRACT) {
    return t("cartPage.report.contract", { contract: packEntry.contractNumber });
  }
  return "";
};
